import { BrowserModule } from "@angular/platform-browser";
import { NgModule } from "@angular/core";
import { AppRoutingModule } from "./app-routing.module";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { MaterialModule } from "./material-module";
import { AuthenticationModule } from "./authentication/authentication.module";

import {
	HttpClientModule,
	HTTP_INTERCEPTORS,
	HttpClient
} from "@angular/common/http";
import { MAT_DATE_LOCALE } from "@angular/material/core";

import { HttpRequestInterceptor } from "./share/service/http.service";
import { ServiceWorkerModule } from "@angular/service-worker";
import { environment } from "../environments/environment";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { FlexLayoutModule } from "@angular/flex-layout";
import { AppDrawerDirective } from "./share/directive/app-drawer.directive";
import { NgxSpinnerModule } from "ngx-spinner";
import { NgxMaskModule } from "ngx-mask";
import { ClipboardModule } from "@angular/cdk/clipboard";
import { CommonModule } from "@angular/common";
import { EmptyRecordModule } from "./share/component/empty-record/empty-record.module";
import { TranslateHttpLoader } from "@ngx-translate/http-loader";
import { TranslateLoader, TranslateModule } from "@ngx-translate/core";
import { CustomLoader } from "./share/service/custom-loader.service";
import { NoCommaPipe } from "./share/pipes/no-comma.pipe";
import { BenefitModule } from "./comparison/ui/benefit/benefit.module";
import { InsurerDetailsModule } from "./comparison/ui/insurer-details/insurer-details.module";
import { CustomPipeModule } from "./share/pipes/custom-pipe.module";
import { NgxMatSelectSearchModule } from "ngx-mat-select-search";
import { MatSelectInfiniteScrollModule } from "ng-mat-select-infinite-scroll";

// Components
import { AppComponent } from "./app.component";
import { AppBlankComponent } from "./layouts/blank/blank.component";
import { AppFullComponent } from "./layouts/full/full.component";
import { AppHeaderComponent } from "./layouts/header/header.component";
import { FooterComponent } from "./layouts/footer/footer.component";
import { AppDrawerComponent } from "./layouts/drawer/drawer.component";
import { ChangePasswordComponent } from "./share/dialog/change-password/change-password.component";
import { ComparisonComponent } from "./comparison/comparison.component";
import { ConfirmationDialog } from "./share/dialog/confirmation-dialog/confirmation.dialog";
import { SingleTextInputDialog } from "./share/dialog/single-text-input-dialog/single-text-input-dialog";
import { InfoDialogComponent } from "./share/dialog/info-dialog/info-dialog.component";
import { BenefitDialogComponent } from "./comparison/ui/benefit-dialog/benefit-dialog.component";
import { CarDetailsDialogComponent } from "./comparison/ui/car-details-dialog/car-details-dialog.component";
import { CarPlanDialogComponent } from "./comparison/ui/car-plan-dialog/car-plan-dialog.component";
import { DealerCaseDialogComponent } from "./comparison/ui/dealer-case-dialog/dealer-case-dialog.component";
import { EditQuotationsComponent } from "./comparison/ui/edit-quotations/edit-quotations.component";
import { SubmitSuccessDialogComponent } from "./share/dialog/submit-success-dialog/submit-success-dialog.component";
import { ThankyouPurchaseDialogComponent } from "./comparison/ui/thankyou-purchase-dialog/thankyou-purchase-dialog.component";
import { ThankyouDialogComponent } from "./share/dialog/thankyou-dialog/thankyou-dialog.component";
import { SampleDialogComponent } from "./share/dialog/sample-dialog/sample-dialog.component";
import { CarVariantDialogComponent } from "./comparison/ui/car-variant-dialog/car-variant-dialog.component";
import { CompanyModule } from "./company/company.module";
import { DismissDialogComponent } from "./share/dialog/dismiss-dialog/dismiss-dialog.component";
import { PaymentOptionsComponent } from "./comparison/ui/payment-options/payment-options.component";
import { EditQuotationsNewComponent } from "./comparison/ui/edit-quotations-new/edit-quotations-new.component";
import { RangeSliderComponent } from "./share/component/range-slider/range-slider.component";
import { InsurerCardComponent } from "./share/component/insurer-card/insurer-card.component";
import { InsurerDetailsV2Component } from "./comparison/ui/insurer-details-v2/insurer-details-v2.component";
import { OtherInsurerCardComponent } from "./share/component/other-insurer-card/other-insurer-card.component";
import { LoadingInsurerCardComponent } from "./share/component/loading-insurer-card/loading-insurer-card.component";
import { EndorsementPaymentComponent } from "./endorsement/screens/endorsement-payment/endorsement-payment.component";
import { SectionTitleComponent } from "./endorsement/ui/section-title/section-title.component";
import { FlexContainerComponent } from "./endorsement/ui/flex-container/flex-container.component";
import { EndorsementPaymentOptionsComponent } from "./endorsement/screens/endorsement-payment-options/endorsement-payment-options.component";
import { NgxMatIntlTelInputComponent } from "ngx-mat-intl-tel-input";
import { AnnouncementModalComponent } from "./share/component/announcement-modal/announcement-modal.component";

export function createTranslateLoader(http: HttpClient) {
	return new TranslateHttpLoader(http, "./assets/i18n", ".json");
}

@NgModule({
	declarations: [
		AppComponent,
		AppBlankComponent,
		AppFullComponent,
		AppHeaderComponent,
		AppDrawerComponent,
		ConfirmationDialog,
		SingleTextInputDialog,
		AppDrawerDirective,
		ChangePasswordComponent,
		InfoDialogComponent,
		FooterComponent,
		ComparisonComponent,
		BenefitDialogComponent,
		CarDetailsDialogComponent,
		CarPlanDialogComponent,
		DealerCaseDialogComponent,
		EditQuotationsComponent,
		SubmitSuccessDialogComponent,
		ThankyouPurchaseDialogComponent,
		ThankyouDialogComponent,
		SampleDialogComponent,
		CarVariantDialogComponent,
		DismissDialogComponent,
		PaymentOptionsComponent,
		EditQuotationsNewComponent,
		RangeSliderComponent,
		InsurerCardComponent,
		InsurerDetailsV2Component,
		OtherInsurerCardComponent,
		LoadingInsurerCardComponent,
		EndorsementPaymentComponent,
		SectionTitleComponent,
		FlexContainerComponent,
		EndorsementPaymentOptionsComponent,
		AnnouncementModalComponent
	],
	imports: [
		BrowserModule,
		NgxMatIntlTelInputComponent,
		AppRoutingModule,
		BrowserAnimationsModule,
		MaterialModule,
		FlexLayoutModule,
		CommonModule,
		EmptyRecordModule,
		AuthenticationModule,
		HttpClientModule,
		NgxSpinnerModule,
		ClipboardModule,
		FormsModule,
		ReactiveFormsModule,
		BenefitModule,
		InsurerDetailsModule,
		CustomPipeModule,
		NgxMatSelectSearchModule,
		CompanyModule,
		MatSelectInfiniteScrollModule,
		NgxMaskModule.forRoot(),
		TranslateModule.forRoot({
			defaultLanguage: "en",
			loader: {
				provide: TranslateLoader,
				useClass: CustomLoader,
				deps: [HttpClient]
			}
		}),
		ServiceWorkerModule.register("/ngsw-worker.js", {
			enabled: environment.production
		})
	],
	providers: [
		{ provide: MAT_DATE_LOCALE, useValue: "en-MY" },
		{
			provide: HTTP_INTERCEPTORS,
			useClass: HttpRequestInterceptor,
			multi: true
		}
	],
	bootstrap: [AppComponent]
})
export class AppModule {}
